import { notify } from '@billy/design-system'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { zervantSettingsQueryKey } from '@features/invoicingSettings/constants/zervantSettingsQueryKey'
import { useUpdateZervantSettings } from '@features/invoicingSettings/hooks/useUpdateZervantSettings'
import { useZervantSettings } from '@features/invoicingSettings/hooks/useZervantSettings'
import { useUpdateOrganizationSettings, useUserOrganization } from '@modules-deprecated/app/organization'

import { queryClient } from '../../../../../../../config/queryClient'
import { useDirtyRoute } from '../../../../../../../contexts/dirtyRouteContext'
import { NotificationKeys } from '../../../../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../../../../enums/queryKeys'
import { useOrganizationUiSetup } from '../../../../../../../hooks/useOrganizationUiSetup'
import { OrganizationZervantSettingsPayload } from '../types/organizationZervantSettingsPayload'
import { OrganizationInfoForm } from '../utils/formData'
import { useUpdateOrganizationInfoSettings } from './useUpdateOrganizationInfoSettings'

interface UpdateOrganizationAndSettingsProps {
  payload: OrganizationInfoForm
  settingsPayload: OrganizationZervantSettingsPayload
}

export const useUpdateOrganizationAndZervantSettings = () => {
  const { t } = useTranslation()
  const [isProcessing, setIsProcessing] = useState(false)

  const { organization } = useUserOrganization()
  const { isUiSetupShineInvoicingAccounting, isUiSetupSupernavInvoicingAccounting } = useOrganizationUiSetup()

  const isZervantFrenchCompany =
    (isUiSetupShineInvoicingAccounting || isUiSetupSupernavInvoicingAccounting) && organization?.countryId === 'FR'

  const { data: zervantSettings } = useZervantSettings({
    enabled: isZervantFrenchCompany,
  })

  const { setDirty } = useDirtyRoute()

  const { update: updateOrganizationAsync } = useUpdateOrganizationInfoSettings()

  const { mutateAsync: updateZervantSettingsAsync } = useUpdateZervantSettings()
  const { mutateAsync: updateOrganizationSettingsAsync } = useUpdateOrganizationSettings()

  const update = async ({ payload, settingsPayload }: UpdateOrganizationAndSettingsProps): Promise<boolean> => {
    if (!organization) {
      return false
    }

    try {
      setIsProcessing(true)
      let zervantPromise
      let settingsPromise

      if (isZervantFrenchCompany && zervantSettings) {
        const isSiretPending = !payload.registrationNo && settingsPayload.isSiretPendingAssignment

        zervantPromise = updateZervantSettingsAsync({
          values: {
            ...zervantSettings?.values,
            company: {
              ...zervantSettings?.values.company,
              legalFormSettings: {
                ...zervantSettings?.values.company.legalFormSettings,
                isSiretPendingAssignment: isSiretPending || false,
                sharedCapital: settingsPayload.sharedCapital,
              },
              legalStatus: settingsPayload.legalStatus,
            },
          },
        })

        settingsPromise = updateOrganizationSettingsAsync({
          organizationId: organization.id,
          key: 'siretPendingAssignmentShowTopBar',
          value: isSiretPending ? '1' : '0',
        })
      }

      const organizationPromise = updateOrganizationAsync(payload)

      await Promise.all([zervantPromise, organizationPromise, settingsPromise])

      setIsProcessing(false)
      notify({
        id: NotificationKeys.OrganizationSettingsUpdate,
        message: t('settings.organization.organization_info.notification.update_success'),
        variant: 'success',
      })
      setDirty(false)

      const promises = [
        queryClient.invalidateQueries(QueryKeys.UserOrganizationSettings),
        queryClient.invalidateQueries([QueryKeys.UserOrganization, organization.id]),
      ]

      if (isZervantFrenchCompany) {
        promises.push(queryClient.invalidateQueries(zervantSettingsQueryKey))
      }

      await Promise.all(promises)

      return true
    } catch {
      notify({ id: NotificationKeys.OrganizationSettingsUpdate, message: t('error_saving'), variant: 'error' })

      setIsProcessing(false)

      return false
    }
  }

  return {
    update,
    isProcessing,
  }
}
